import { R4PreTriageApplyTaskBuilder } from '@adapters/fhir/R4PreTriageApplyTaskBuilder';
import { R4PreTriageOutcomeParser } from '@adapters/fhir/R4PreTriageOutcomeParser';
import { R4TriageQuestionnaireParser } from '@adapters/fhir/R4TriageQuestionnaireParser';
import { R4QuestionnaireResponseBuilder } from '@adapters/fhir/R4QuestionnaireResponseBuilder';
import {
  type PreTriageLoadResultViewModel,
  type PreTriageSubmissionResultViewModel,
} from '@adapters/presenters/PreTriagePresenter';
import { PreTriagePresenter } from '@adapters/presenters/PreTriagePresenter';
import type { AppEntryContext } from '@core/entities/AppEntryContext';
import type { TriageAnswerRecord, TriageAnswerValue, TriageChoiceOption, TriageQuestion, TriageQuestionnaire } from '@core/entities/Triage';
import { GuidanceResponseStatusEnum } from '@core/fhir-r4/fhir';
import { ApplyPreTriageTaskUseCase } from '@core/usecases/ApplyPreTriageTaskUseCase';
import { GetAppEntryContextUseCase } from '@core/usecases/GetAppEntryContextUseCase';
import { GetTriageDeferredOptionsUseCase } from '@core/usecases/GetTriageDeferredOptionsUseCase';
import { JourneyDraftStore } from '@infrastructure/storage/JourneyDraftStore';

export class PreTriageController {
  private static readonly patientSexLinkId = 'sex-at-birth';

  private questionnaire?: TriageQuestionnaire;

  private appEntryContextPromise?: Promise<AppEntryContext>;
  private activeStepId?: string;

  public constructor(
    private readonly applyPreTriageTaskUseCase: ApplyPreTriageTaskUseCase,
    private readonly questionnaireParser: R4TriageQuestionnaireParser,
    private readonly questionnaireResponseBuilder: R4QuestionnaireResponseBuilder,
    private readonly applyTaskBuilder: R4PreTriageApplyTaskBuilder,
    private readonly outcomeParser: R4PreTriageOutcomeParser,
    private readonly preTriagePresenter: PreTriagePresenter,
    private readonly journeyDraftStore: JourneyDraftStore,
    private readonly getAppEntryContextUseCase: GetAppEntryContextUseCase,
    private readonly getTriageDeferredOptionsUseCase?: GetTriageDeferredOptionsUseCase,
  ) {}

  public async load(): Promise<PreTriageLoadResultViewModel> {
    const restoredStep = this.journeyDraftStore.getActiveLiveTriageStep();

    if (restoredStep) {
      this.questionnaire = restoredStep.questionnaire;
      this.activeStepId = restoredStep.stepId;
      return this.preTriagePresenter.presentQuestionnaire(this.questionnaire);
    }

    if (!restoredStep && this.journeyDraftStore.hasLiveTriageState()) {
      this.journeyDraftStore.markCorruptedState();
    }

    try {
      const gatewayResult = await this.applyPreTriageTaskUseCase.execute(this.applyTaskBuilder.buildInitial());
      const { guidanceResponse } = gatewayResult;

      if (guidanceResponse.status === GuidanceResponseStatusEnum.SUCCESS) {
        return this.preTriagePresenter.presentLoadOutcome(this.outcomeParser.parse(gatewayResult));
      }

      this.questionnaire = this.questionnaireParser.parse(guidanceResponse);
      this.activeStepId = this.getStepId(this.questionnaire);
      this.journeyDraftStore.initializeLiveTriageStep(this.activeStepId, this.questionnaire);
      return this.preTriagePresenter.presentQuestionnaire(this.questionnaire);
    } catch (error) {
      return this.preTriagePresenter.presentFailure(error);
    }
  }

  public getCurrentAnswers(): TriageAnswerRecord {
    if (!this.activeStepId) {
      return {};
    }

    return this.journeyDraftStore.getLiveTriageAnswers(this.activeStepId);
  }

  public async submit(answers: TriageAnswerRecord): Promise<PreTriageSubmissionResultViewModel> {
    if (!this.questionnaire || !this.activeStepId) {
      throw new Error('Reload the triage start page before submitting answers.');
    }

    if (!this.questionnaire.workflowStepIdentity) {
      throw new Error('Reload the triage start page before submitting answers.');
    }

    const normalizedAnswers = this.normalizeAnswersForCurrentQuestionnaire(answers);
    this.journeyDraftStore.recordLiveTriageAnswers(this.activeStepId, normalizedAnswers);

    try {
      const questionnaireResponse = this.questionnaireResponseBuilder.build(this.questionnaire, normalizedAnswers);
      const appEntryContext = await this.getAppEntryContext();
      const taskPayload = this.applyTaskBuilder.buildContinuation(
        this.questionnaire.encounterId,
        questionnaireResponse,
        this.questionnaire.workflowStepIdentity,
        {
          patientId: appEntryContext.proxyContext.patientIdentifier ?? undefined,
          birthDate: appEntryContext.patientBirthDate ?? undefined,
          gender: this.getPatientSexAnswer(normalizedAnswers),
        },
      );
      const gatewayResult = await this.applyPreTriageTaskUseCase.execute(taskPayload);
      const { guidanceResponse } = gatewayResult;

      if (guidanceResponse.status === GuidanceResponseStatusEnum.DATA_REQUIRED || guidanceResponse.status === GuidanceResponseStatusEnum.DATA_REQUESTED) {
        const previousStepId = this.activeStepId;
        this.questionnaire = this.questionnaireParser.parse(guidanceResponse);
        this.activeStepId = this.getStepId(this.questionnaire);
        const wouldCycle = this.journeyDraftStore.wouldAdvanceCreateCycle(previousStepId, this.activeStepId);
        if (wouldCycle) {
          this.journeyDraftStore.initializeLiveTriageStep(this.activeStepId, this.questionnaire);
        } else {
          this.journeyDraftStore.advanceLiveTriageStep(previousStepId, this.activeStepId, this.questionnaire);
        }
        return this.preTriagePresenter.presentNextQuestionnaire(this.questionnaire);
      }

      return this.preTriagePresenter.presentSubmissionOutcome(this.outcomeParser.parse(gatewayResult));
    } catch (error) {
      return this.preTriagePresenter.presentSubmissionFailure(error);
    }
  }

  public async loadDeferredOptions(linkId: string, query?: string): Promise<readonly TriageChoiceOption[]> {
    if (!this.questionnaire) {
      throw new Error('Reload the triage start page before loading deferred options.');
    }

    const question = this.findQuestion(this.questionnaire.questions, linkId);
    if (!question) {
      throw new Error(`Question '${linkId}' was not found in the active triage questionnaire.`);
    }

    const sourceListUrl = question.metadata?.sourceListUrl;
    if (!sourceListUrl || !this.isDeferredSourceListUrl(sourceListUrl) || !this.getTriageDeferredOptionsUseCase) {
      return question.options ?? [];
    }

    const normalizedQuery = query?.trim();
    if (!normalizedQuery) {
      this.questionnaire = this.updateQuestionnaireOptions(this.questionnaire, linkId, []);
      this.clearStoredAnswer(linkId);
      this.persistActiveQuestionnaire();
      return [];
    }

    const result = await this.getTriageDeferredOptionsUseCase.execute(sourceListUrl, normalizedQuery);
    this.questionnaire = this.updateQuestionnaireOptions(this.questionnaire, linkId, result.items);
    this.persistActiveQuestionnaire();
    return result.items;
  }

  private findQuestion(questions: readonly TriageQuestion[], linkId: string): TriageQuestion | undefined {
    for (const question of questions) {
      if (question.linkId === linkId) {
        return question;
      }

      const childMatch = question.children ? this.findQuestion(question.children, linkId) : undefined;
      if (childMatch) {
        return childMatch;
      }
    }

    return undefined;
  }

  private updateQuestionnaireOptions(
    questionnaire: TriageQuestionnaire,
    linkId: string,
    options: readonly TriageChoiceOption[],
  ): TriageQuestionnaire {
    const updatedQuestions = questionnaire.questions.map((question) => this.updateQuestionOptions(question, linkId, options));

    return {
      ...questionnaire,
      questions: updatedQuestions,
    };
  }

  private updateQuestionOptions(
    question: TriageQuestion,
    linkId: string,
    options: readonly TriageChoiceOption[],
  ): TriageQuestion {
    if (question.linkId === linkId) {
      return {
        ...question,
        options,
      };
    }

    if (!question.children) {
      return question;
    }

    return {
      ...question,
      children: question.children.map((child) => this.updateQuestionOptions(child, linkId, options)),
    };
  }

  private isDeferredSourceListUrl(sourceListUrl: string): boolean {
    return sourceListUrl.startsWith('/api/triage/');
  }

  private persistActiveQuestionnaire(): void {
    if (!this.questionnaire || !this.activeStepId) {
      return;
    }

    this.journeyDraftStore.advanceLiveTriageStep(this.activeStepId, this.activeStepId, this.questionnaire);
  }

  private getPatientSexAnswer(answers: TriageAnswerRecord): 'male' | 'female' | undefined {
    const answer = answers[PreTriageController.patientSexLinkId];
    return answer === 'male' || answer === 'female'
      ? answer
      : undefined;
  }

  private normalizeAnswersForCurrentQuestionnaire(answers: TriageAnswerRecord): TriageAnswerRecord {
    const questions = this.flattenQuestions(this.questionnaire?.questions ?? []);
    const knownLinkIds = new Set(questions.map((q) => q.linkId));

    const normalizedAnswers = this.filterKnownAnswers(answers, knownLinkIds);

    for (const question of questions) {
      this.normalizeQuestionAnswer(normalizedAnswers, question);
    }

    return normalizedAnswers;
  }

  private filterKnownAnswers(answers: TriageAnswerRecord, knownLinkIds: ReadonlySet<string>): Record<string, TriageAnswerValue> {
    return Object.fromEntries(
      Object.entries(answers).filter(([linkId]) =>
        knownLinkIds.has(linkId) || linkId === PreTriageController.patientSexLinkId,
      ),
    );
  }

  private normalizeQuestionAnswer(answers: Record<string, TriageAnswerValue>, question: TriageQuestion): void {
    if (!question.options) {
      return;
    }

    const rawAnswer = answers[question.linkId];
    if (rawAnswer === undefined) {
      return;
    }

    const allowedCodes = new Set(question.options.map((option) => option.code));
    if (Array.isArray(rawAnswer)) {
      this.setNormalizedArrayAnswer(answers, question.linkId, rawAnswer, allowedCodes);
      return;
    }

    if (typeof rawAnswer === 'string' && !allowedCodes.has(rawAnswer)) {
      delete answers[question.linkId];
    }
  }

  private setNormalizedArrayAnswer(
    answers: Record<string, TriageAnswerValue>,
    linkId: string,
    rawAnswer: readonly string[],
    allowedCodes: ReadonlySet<string>,
  ): void {
    const filtered = rawAnswer.filter((value) => allowedCodes.has(value));
    if (filtered.length === 0) {
      delete answers[linkId];
      return;
    }

    answers[linkId] = filtered;
  }

  private clearStoredAnswer(linkId: string): void {
    if (!this.activeStepId) {
      return;
    }

    const storedAnswers = this.journeyDraftStore.getLiveTriageAnswers(this.activeStepId);
    if (!(linkId in storedAnswers)) {
      return;
    }

    const updatedAnswers: Record<string, TriageAnswerValue> = { ...storedAnswers };
    delete updatedAnswers[linkId];
    this.journeyDraftStore.recordLiveTriageAnswers(this.activeStepId, updatedAnswers);
  }

  private flattenQuestions(questions: readonly TriageQuestion[]): readonly TriageQuestion[] {
    return questions.flatMap((question) => [
      question,
      ...this.flattenQuestions(question.children ?? []),
    ]);
  }

  private getAppEntryContext(): Promise<AppEntryContext> {
    let appEntryContextPromise = this.appEntryContextPromise;
    if (!appEntryContextPromise) {
      appEntryContextPromise = this.getAppEntryContextUseCase.execute()
        .catch((error) => {
          this.appEntryContextPromise = undefined;
          throw error;
        });
      this.appEntryContextPromise = appEntryContextPromise;
    }

    return appEntryContextPromise;
  }

  private getStepId(questionnaire: TriageQuestionnaire): string {
    if (!questionnaire.workflowStepIdentity) {
      throw new Error('The triage questionnaire did not include workflow step identity.');
    }

    const { journeyRevision, stepSequence, questionnaireEventSequence } = questionnaire.workflowStepIdentity;
    // questionnaireEventSequence must be included: journeyRevision/stepSequence can restart
    // (e.g. the pre-triage → main-triage boundary), and without it the step key collides
    // with an earlier visit, forcing a destructive back-link chain reset.
    return `${questionnaire.encounterId}:${journeyRevision}:${stepSequence}:${questionnaireEventSequence}`;
  }
}